"use client";

import { useEffect, useState } from "react";
import { clsx } from "clsx";

const navItems = [
  { href: "#overview", id: "overview", label: "Overview" },
  { href: "#paid", id: "paid", label: "Paid" },
  { href: "#product", id: "product", label: "Product" },
  { href: "#organic", id: "organic", label: "Organic" },
  { href: "#crm", id: "crm", label: "CRM" },
  { href: "#partners", id: "partners", label: "Partners" },
  { href: "#career", id: "career", label: "Career" },
] as const;

const spyIds = [...navItems.map((item) => item.id), "intro", "contact"] as const;

export function Navigation() {
  const [activeId, setActiveId] = useState<string>("intro");

  useEffect(() => {
    const sections = spyIds
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => Boolean(el));

    if (sections.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort(
            (a, b) => (b.intersectionRatio ?? 0) - (a.intersectionRatio ?? 0)
          );

        if (visible[0]?.target?.id) {
          setActiveId(visible[0].target.id);
        }
      },
      {
        rootMargin: "-20% 0px -55% 0px",
        threshold: [0, 0.25, 0.5, 0.75],
      }
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  return (
    <header className="sticky top-0 z-50 border-b border-[--color-border] bg-[--color-bg]/95 backdrop-blur-sm">
      <nav className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-4 sm:px-6 lg:px-8">
        <a
          href="#intro"
          className="shrink-0 font-mono text-lg font-semibold tracking-tight text-[--color-text] transition-colors hover:text-[--color-accent]"
        >
          matteo_mariat
        </a>

        <div className="flex min-w-0 flex-1 items-center justify-end gap-3 sm:gap-6">
          <div className="flex items-center gap-1 overflow-x-auto sm:gap-5">
            {navItems.map((item) => {
              const isActive = activeId === item.id;
              return (
                <a
                  key={item.id}
                  href={item.href}
                  className={clsx(
                    "whitespace-nowrap border-b-2 px-1 py-1 text-sm transition-colors",
                    isActive
                      ? "border-[--color-accent] text-[--color-text]"
                      : "border-transparent text-[--color-text-secondary] hover:text-[--color-text]"
                  )}
                >
                  {item.label}
                </a>
              );
            })}
          </div>

          <a
            href="#contact"
            className="shrink-0 rounded-md border border-black bg-black px-3 py-1.5 text-sm font-medium text-white transition-opacity hover:opacity-80"
          >
            Contact
          </a>
        </div>
      </nav>
    </header>
  );
}
